import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Flex,
  Modal,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import { IconBug, IconEdit, IconReload } from "@tabler/icons";
import { TeaAddonOperation } from "entities/tea_addon_renewal_run";
import { colorsByStatus } from "entities/tea_addon_renewal_run/operationStatus";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

type OperationResponse = {
  success: boolean;
  error?: string;
};

type TeaAddonOperationsProps = {
  tea_addon_operations: TeaAddonOperation[];
  onOperation: Function;
};

export const TeaAddonOperations = ({
  tea_addon_operations,
  onOperation,
}: TeaAddonOperationsProps) => {
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isNoteOpen, setNoteOpen] = useState<boolean>(false);
  const [selectedOperation, setSelectedOperation] =
    useState<TeaAddonOperation | null>(null);
  const [operationNote, setOperationNote] = useState<string>("");
  const apiUrl = useApiUrl();

  const { mutate: retryOperation, isLoading: isRetrying } =
    useCustomMutation<OperationResponse>();

  const { mutate: updateOperationNote, isLoading: isUpdatingOperationNote } =
    useCustomMutation<OperationResponse>();

  useEffect(() => {
    setOperationNote(selectedOperation?.notes || "");
  }, [selectedOperation?.id]);

  return (
    <>
      <Modal
        opened={isErrorOpen}
        onClose={() => setErrorOpen(false)}
        title="Error"
        size="80%"
      >
        <Text color="red">
          <pre>{errorMessage}</pre>
        </Text>
      </Modal>
      <Modal
        opened={isNoteOpen}
        onClose={() => {
          setNoteOpen(false);
          setSelectedOperation(null);
        }}
        size="lg"
        title="Notes"
      >
        <Textarea
          value={operationNote}
          onChange={(e) => setOperationNote(e.target.value)}
        />
        <Button
          mt="lg"
          disabled={isUpdatingOperationNote}
          onClick={() =>
            selectedOperation?.id &&
            updateOperationNote(
              {
                url: `${apiUrl}/tea_addon_operations/${selectedOperation.id}`,
                method: "patch",
                values: { content: operationNote },
              },
              {
                onSuccess: () => {
                  setNoteOpen(false);
                  setSelectedOperation(null);
                  onOperation();
                },
              },
            )
          }
        >
          Save
        </Button>
      </Modal>

      <Table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Status</th>
            <th>User</th>
            <th>Tea Subscription</th>
            <th>Tea Addon</th>
            <th>Runs Count</th>
            <th>Last Run At</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {tea_addon_operations.map((tea_addon_operation) => {
            const showActions =
              tea_addon_operation.status !== "succeeded" &&
              tea_addon_operation.error_message;

            return (
              <tr>
                <td>{tea_addon_operation.id}</td>
                <td>
                  <Badge color={colorsByStatus[tea_addon_operation.status]}>
                    {tea_addon_operation.status}
                  </Badge>
                </td>
                <td>
                  <Anchor
                    component={Link}
                    to={`/users/show/${tea_addon_operation.user.id}`}
                  >
                    {tea_addon_operation.user?.email}
                  </Anchor>
                </td>
                <td>
                  <Anchor
                    component={Link}
                    to={`/tea_addon_subscriptions/show/${tea_addon_operation.tea_addon_subscription.id}`}
                  >
                    {tea_addon_operation.tea_addon_subscription?.id}
                  </Anchor>
                </td>
                <td>
                  {tea_addon_operation.tea_addon ? (
                    <Anchor
                      component={Link}
                      to={`/tea_addons/show/${tea_addon_operation.tea_addon.id}`}
                    >
                      {tea_addon_operation.tea_addon.name}
                    </Anchor>
                  ) : (
                    <Badge color="gray">-</Badge>
                  )}
                </td>
                <td>{tea_addon_operation.runs_count}</td>
                <td>{tea_addon_operation.last_run_at?.toString()}</td>
                <td>{tea_addon_operation.created_at.toString()}</td>
                <td>
                  <Flex gap="xs">
                    <ActionIcon
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNoteOpen(true);
                        setSelectedOperation(tea_addon_operation);
                      }}
                    >
                      <IconEdit />
                    </ActionIcon>

                    {showActions ? (
                      <>
                        <ActionIcon
                          color="red"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setErrorOpen(true);
                            setErrorMessage(tea_addon_operation.error_message);
                          }}
                        >
                          <IconBug />
                        </ActionIcon>
                        <ActionIcon
                          color="blue"
                          variant="outline"
                          disabled={isRetrying}
                          size="sm"
                          onClick={() => {
                            retryOperation(
                              {
                                url: `${apiUrl}/tea_addon_operations/${tea_addon_operation.id}/retry`,
                                method: "post",
                                values: {},
                              },
                              {
                                onSuccess: () => onOperation(),
                                onError: () => onOperation(),
                              },
                            );
                          }}
                        >
                          <IconReload />
                        </ActionIcon>
                      </>
                    ) : (
                      <div />
                    )}
                  </Flex>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};
